/**
 * Sub-parts for InvoiceFormAddresses to keep the main file under the line/complexity limits.
 */

import type { Address } from '@/gen/proto'

import { MODE_OF_TRANSPORT_OPTIONS } from './invoice-form-helpers'
import { addressHasUsage } from '../../../utils/billing-helpers'
import { DatePicker, FormField, Input, Select } from '../../ui'
import { AddressCard } from '../AddressCard'

import type { UseInvoiceFormReturn } from './useInvoiceForm'

interface AddressCardWithActionsProps {
  address: Address
  onChangeClick: () => void
  onRemove: () => void
}

export function AddressCardWithActions({ address, onChangeClick, onRemove }: AddressCardWithActionsProps): JSX.Element {
  return (
    <div className="relative group">
      <AddressCard selected address={address} showActions={false} onSelect={() => {}} />
      <div className="absolute bottom-2 right-2 flex gap-2">
        <button
          className="px-3 py-1 text-xs font-medium text-primary bg-surface border border-border rounded hover:bg-muted shadow-sm"
          type="button"
          onClick={onChangeClick}
        >
          Change
        </button>
        <button
          className="px-3 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-surface border border-red-300 dark:border-red-700 rounded hover:bg-red-50 dark:hover:bg-red-900/30 shadow-sm"
          type="button"
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
    </div>
  )
}

interface EmptyAddressButtonProps {
  label: string
  onClick: () => void
  icon?: 'location' | 'shipping'
}

export function EmptyAddressButton({ label, onClick, icon = 'location' }: EmptyAddressButtonProps): JSX.Element {
  return (
    <button
      className="w-full p-4 border-2 border-dashed border-border rounded-lg hover:border-primary hover:bg-muted transition-colors text-muted-foreground hover:text-primary flex flex-col items-center justify-center gap-2 min-h-[140px]"
      type="button"
      onClick={onClick}
    >
      <div className="p-3 rounded-lg bg-accent text-muted-foreground">
        {icon === 'location' ? <LocationIcon /> : <ShippingIcon />}
      </div>
      <span className="text-sm font-medium">{label}</span>
    </button>
  )
}

function LocationIcon(): JSX.Element {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
      <path
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
    </svg>
  )
}

function ShippingIcon(): JSX.Element {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
    </svg>
  )
}

export function getShippingEmptyLabel(
  isVendor: boolean,
  orgAddresses: Address[],
  allPartyAddresses: Address[],
  invoiceType: string,
): string {
  if (isVendor) {
    return orgAddresses.length > 0
      ? 'Click to Select Delivery Address'
      : 'No company addresses found — add in Company Setup'
  }
  const hasShipping =
    allPartyAddresses.filter((a) =>
      invoiceType === 'export' ? addressHasUsage(a, 'consignee') : addressHasUsage(a, 'shipping'),
    ).length > 0
  const hasBilling = Boolean(allPartyAddresses.find((a) => addressHasUsage(a, 'billing')))
  return hasShipping || hasBilling ? 'Click to Select or Add Address' : 'Click to Add Address'
}

interface PortFieldsProps {
  isLoading: boolean
  portOfLoading: string
  setPortOfLoading: (v: string) => void
  portOfDischarge: string
  setPortOfDischarge: (v: string) => void
  portCode: string
  setPortCode: (v: string) => void
}

function PortFields(p: PortFieldsProps): JSX.Element {
  return (
    <>
      <FormField hideMessage label="Port of Loading">
        <Input
          hideMessage
          disabled={p.isLoading}
          placeholder="INMUN1 (Mumbai Port)"
          type="text"
          value={p.portOfLoading}
          onChange={(e) => p.setPortOfLoading(e.target.value)}
        />
      </FormField>
      <FormField hideMessage label="Port of Discharge">
        <Input
          hideMessage
          disabled={p.isLoading}
          placeholder="USLAX1 (Los Angeles)"
          type="text"
          value={p.portOfDischarge}
          onChange={(e) => p.setPortOfDischarge(e.target.value)}
        />
      </FormField>
      <FormField hideMessage label="Port Code">
        <Input
          hideMessage
          disabled={p.isLoading}
          placeholder="INDEL4"
          type="text"
          value={p.portCode}
          onChange={(e) => p.setPortCode(e.target.value)}
        />
      </FormField>
    </>
  )
}

interface ShippingBillFieldsProps {
  isLoading: boolean
  shippingBillNumber: string
  setShippingBillNumber: (v: string) => void
  shippingBillDate: Date | null
  setShippingBillDate: (v: Date | null) => void
  dateFormat: string
}

function ShippingBillFields(p: ShippingBillFieldsProps): JSX.Element {
  return (
    <>
      <FormField hideMessage label="Shipping Bill Number">
        <Input
          hideMessage
          disabled={p.isLoading}
          placeholder="SB1234567"
          type="text"
          value={p.shippingBillNumber}
          onChange={(e) => p.setShippingBillNumber(e.target.value)}
        />
      </FormField>
      <FormField hideMessage label="Shipping Bill Date">
        <DatePicker
          dateFormat={p.dateFormat}
          disabled={p.isLoading}
          value={p.shippingBillDate}
          onChange={p.setShippingBillDate}
        />
      </FormField>
    </>
  )
}

interface TransportFieldsProps {
  isLoading: boolean
  modeOfTransport: string
  setModeOfTransport: (v: 'sea' | 'air' | 'road' | 'rail') => void
  vesselFlightNumber: string
  setVesselFlightNumber: (v: string) => void
  blAwbNumber: string
  setBlAwbNumber: (v: string) => void
}

function TransportFields(p: TransportFieldsProps): JSX.Element {
  const isAir = p.modeOfTransport === 'air'
  return (
    <>
      <FormField hideMessage label="Mode of Transport">
        <Select
          hideMessage
          disabled={p.isLoading}
          options={MODE_OF_TRANSPORT_OPTIONS}
          value={p.modeOfTransport}
          onChange={(value) => p.setModeOfTransport(value as 'sea' | 'air' | 'road' | 'rail')}
        />
      </FormField>
      <FormField hideMessage label={isAir ? 'Flight Number' : 'Vessel Number'}>
        <Input
          hideMessage
          disabled={p.isLoading}
          placeholder={isAir ? 'AI101' : 'MV MAERSK'}
          type="text"
          value={p.vesselFlightNumber}
          onChange={(e) => p.setVesselFlightNumber(e.target.value)}
        />
      </FormField>
      <FormField hideMessage label={isAir ? 'AWB Number' : 'B/L Number'}>
        <Input
          hideMessage
          disabled={p.isLoading}
          placeholder={isAir ? 'AWB12345678' : 'MAEU123456789'}
          type="text"
          value={p.blAwbNumber}
          onChange={(e) => p.setBlAwbNumber(e.target.value)}
        />
      </FormField>
    </>
  )
}

interface ContainerFieldProps {
  isLoading: boolean
  containerNumbers: string
  setContainerNumbers: (v: string) => void
}

function ContainerField({ isLoading, containerNumbers, setContainerNumbers }: ContainerFieldProps): JSX.Element {
  return (
    <FormField hideMessage label="Container Numbers">
      <Input
        hideMessage
        disabled={isLoading}
        placeholder="MSCU1234567, MSCU1234568"
        type="text"
        value={containerNumbers}
        onChange={(e) => setContainerNumbers(e.target.value)}
      />
    </FormField>
  )
}

interface ShippingDocumentationProps {
  form: UseInvoiceFormReturn
  isLoading: boolean
}

function ShippingDocumentationFields({ form, isLoading }: ShippingDocumentationProps): JSX.Element {
  const f = form
  const isSea = f.modeOfTransport === 'sea'
  return (
    <div className="form-grid el-gap-field">
      <PortFields
        isLoading={isLoading}
        portCode={f.portCode}
        portOfDischarge={f.portOfDischarge}
        portOfLoading={f.portOfLoading}
        setPortCode={f.setPortCode}
        setPortOfDischarge={f.setPortOfDischarge}
        setPortOfLoading={f.setPortOfLoading}
      />
      <ShippingBillFields
        dateFormat={f.dateFormat}
        isLoading={isLoading}
        setShippingBillDate={f.setShippingBillDate}
        setShippingBillNumber={f.setShippingBillNumber}
        shippingBillDate={f.shippingBillDate}
        shippingBillNumber={f.shippingBillNumber}
      />
      <TransportFields
        blAwbNumber={f.blAwbNumber}
        isLoading={isLoading}
        modeOfTransport={f.modeOfTransport}
        setBlAwbNumber={f.setBlAwbNumber}
        setModeOfTransport={f.setModeOfTransport}
        setVesselFlightNumber={f.setVesselFlightNumber}
        vesselFlightNumber={f.vesselFlightNumber}
      />
      {isSea && (
        <ContainerField
          containerNumbers={f.containerNumbers}
          isLoading={isLoading}
          setContainerNumbers={f.setContainerNumbers}
        />
      )}
    </div>
  )
}

export function ShippingDocumentation({ form, isLoading }: ShippingDocumentationProps): JSX.Element {
  return (
    <div className="card el-p-md">
      <h3 className="text-xs font-bold tracking-wider uppercase -mx-3 -mt-[10px] mb-[10px] px-3 py-2 bg-primary-solid text-primary-foreground rounded-t">
        Shipping Documentation
      </h3>
      <ShippingDocumentationFields form={form} isLoading={isLoading} />
    </div>
  )
}
